import { useEffect, useState } from "react";
import { Button, Checkbox, Grid } from "@mui/material";
import { planMapColumns } from "./map-columns";
import { planApiKeyStore } from "../../../../../api/plan-api-key-store";
import { useLoadData } from "../../../../../hooks/useLoadData";
import { ApiResponse } from "../../../../../models/api-response";
import { useSaveData } from "../../../../../hooks/useSaveData";
import { SiTrackGrid } from "../../../../../components/si-track-grid/si-track-grid";
import { UserPermissionName } from "../../../../../models/user-permission-name";
import { useAuthorize } from "../../../../../hooks/use-authorize";


interface CellProps {
    planData: any;
}

interface CustomGridCellProps {
    field: string;
}

interface PlanRequestArgs {
    customerID?: number;
    projectID?: number;
    officeID?: number;
    unitID?: number;
}

interface MapPlanComponent {
    planData: any[];
    planRequestArgs: PlanRequestArgs;
    onClosed: () => void;
}

export const PlanMappedComponent = ({ planData, planRequestArgs, onClosed }: MapPlanComponent) => {

    const [selectedPlan, setSelectedPlan] = useState<any[]>([]);
    const [completeData, setCompleteData] = useState<any[]>([]);
    const { data, isLoading, isFetching } = useLoadData<ApiResponse<any[]>>(
        planApiKeyStore.getPlanMapList({ customerID: planRequestArgs?.customerID, officeID: planRequestArgs?.officeID, mapLevel: 'office' })
    );

    const { isLoading: isMapPlanLoading, mutateAsync } = useSaveData<any>(planApiKeyStore.mapPlans(),
        () => planApiKeyStore.getPlans({
            officeID: planRequestArgs.officeID,
        }));

    useEffect(() => {
        if (data) {
            const updatedData = data?.data.map((d) => {
                const matchingPlan = planData?.find((s) => s.planID === d.planID);
                return {
                    ...d,
                    isSelected: !!matchingPlan,
                    isPlanReview: matchingPlan?.planReviewTypeID === 1,
                    isRebateProcess: matchingPlan?.planReviewTypeID === 2,
                };
            });
            const uniquePlans = updatedData?.filter(
                (item, index, self) => index === self.findIndex((t) => t.planID === item.planID)
            );
            setSelectedPlan(uniquePlans?.filter((p: any) => p.isSelected === true));
            setCompleteData(uniquePlans);
        }
    }, [data]);

    const handleSelectedItem = (values: any[]) => {
        const updatedData = completeData?.map((d) => {
            const matchingPlan = values.some((s) => s.planID === d.planID);
            return {
                ...d,
                isSelected: matchingPlan,
                ...(matchingPlan === false && { isPlanReview: false, isRebateProcess: false }),
            };
        });
        setCompleteData(updatedData)
        setSelectedPlan(values);
    };

    const handleChangePlanReview = (planID: any) => {
        setSelectedPlan((prevPlans: any) =>
            prevPlans.map((plan: any) =>
                plan.planID === planID ? { ...plan, isPlanReview: !plan.isPlanReview, isRebateProcess: false } : plan
            )
        );
        setCompleteData((prevPlans: any) =>
            prevPlans.map((plan: any) =>
                plan.planID === planID ? { ...plan, isPlanReview: !plan.isPlanReview, isRebateProcess: false } : plan
            )
        );
    };

    const handleChangeRebateProcess = (planID: any) => {
        setSelectedPlan((prevPlans: any) =>
            prevPlans.map((plan: any) =>
                plan.planID === planID ? { ...plan, isRebateProcess: !plan.isRebateProcess, isPlanReview: false, } : plan
            )
        );
        setCompleteData((prevPlans: any) =>
            prevPlans.map((plan: any) =>
                plan.planID === planID ? { ...plan, isRebateProcess: !plan.isRebateProcess, isPlanReview: false, } : plan
            )
        );
    };

    const PlanReview = ({ planData }: CellProps) => {
        const onCheckboxChange = () => {
            handleChangePlanReview(planData?.planID)
        }
        return (
            <td>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Checkbox checked={planData?.isPlanReview} onChange={onCheckboxChange} />
                </div>
            </td>
        );
    };

    const RebateProcess = ({ planData }: CellProps) => {
        const onCheckboxChange = () => {
            handleChangeRebateProcess(planData?.planID)
        }
        return (
            <td>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Checkbox checked={planData?.isRebateProcess} onChange={onCheckboxChange} />
                </div>
            </td>
        );
    };


    const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
        if (field === 'planReview') {
            return (dataItem: any) => {
                return <PlanReview planData={dataItem.dataItem} />;
            };
        }

        if (field === 'rebateProcess') {
            return (dataItem: any) => {
                return <RebateProcess planData={dataItem.dataItem} />;
            };
        }
        // Return undefined if the field is not 'bluePrint'
        return undefined;
    };

    const submitData = async () => {

        const transformedPlans = selectedPlan.map((plan: any) => ({
            planID: plan.planID,
            planReviewTypeID: plan.isPlanReview ? 1 : plan.isRebateProcess ? 2 : 0,
        }));

        const mappedData = {
            officeIDs: [planRequestArgs?.officeID],
            plans: transformedPlans
        }

        await mutateAsync({ data: mappedData });
        onClosed();
    }

    const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

    return (
        <Grid container spacing={2}>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
                <SiTrackGrid
                    fileTitle='office_map_plan'
                    dataItems={completeData}
                    selectedField="isSelected"
                    dataItemKey="planID"
                    columns={planMapColumns}
                    // customCell={(field) =>
                    //     CustomGridCell({
                    //         field,
                    //     })
                    // }
                    onSelectedItems={handleSelectedItem}
                    actionPermissions={{
                        export: UserPermissionName.DOWNLOAD_GRID
                    }}
                    isBusy={isLoading || isMapPlanLoading}
                />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12} className="spaceBetween">
                <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
                    Cancel
                </Button>
                <Button
                    type="button"
                    variant="contained"
                    style={{ width: '130px' }}
                    onClick={submitData}
                    disabled={selectedPlan?.length == 0 || isReadOnlyPermission}
                >
                    Save
                </Button>
            </Grid>
        </Grid>
    );
}